import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import React from 'react';

import { Box } from '@mui/material';

import {
  Job,
  JobApplication,
  JobMatcherApplicationStatusEnum,
} from '@libs/graphql-types';
import { modelPath } from '@libs/helpers/form';
import Button from '@libs/ui/components/button';
import { ConnectedTextField } from '@libs/ui/components/form/text-field';
import Typography from '@libs/ui/components/typography';

import { INITIAL_VALUES } from './consts';
import { useApplyForAction, useModalValidationSchema } from './hooks';
import { FormState } from './types';

interface ApplyFormProps {
  job: Job;
  application?: JobApplication;
  applicationStatus: JobMatcherApplicationStatusEnum;
}

const ApplyForm = ({
  job,
  application,
  applicationStatus,
}: ApplyFormProps) => {
  const { t } = useTranslation('talentMatcher');
  const { onSubmit, isLoading } = useApplyForAction(job.id);
  const { schema } = useModalValidationSchema();
  const title =
    applicationStatus === 'REJECTED'
      ? t('sourceJob.rejectedTitle')
      : t('sourceJob.noSeatsTitle');

  return (
    <Box maxWidth={500} margin="0 auto">
      <Box pb={6}>
        <Typography textAlign="center" variant="h5">
          {title}
        </Typography>
      </Box>
      <Box pb={6}>
        <Typography fontWeight={500} textAlign="center" variant="h6">
          {t('sourceJob.stillPresent')}
        </Typography>
      </Box>
      <Box pb={4}>
        <Typography paragraph variant="body1" color="textSecondary">
          {t('sourceJob.inviteBody')}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {t('sourceJob.inviteCandidate')}
        </Typography>
      </Box>
      <Formik<FormState>
        validationSchema={schema}
        onSubmit={onSubmit}
        initialValues={INITIAL_VALUES}
      >
        {() => (
          <Form>
            <Box mb={4}>
              <ConnectedTextField
                size="medium"
                name={modelPath<FormState>((m) => m.firstName)}
                fullWidth
                variant="filled"
                label={t('sourceJob.firstName')}
              />
            </Box>
            <Box mb={4}>
              <ConnectedTextField
                size="medium"
                name={modelPath<FormState>((m) => m.lastName)}
                fullWidth
                variant="filled"
                label={t('sourceJob.lastName')}
              />
            </Box>
            <Box mb={4}>
              <ConnectedTextField
                size="medium"
                name={modelPath<FormState>((m) => m.email)}
                fullWidth
                variant="filled"
                label={t('sourceJob.email')}
              />
            </Box>
            <Box pb={8}>
              <Button
                type="submit"
                size="large"
                fullWidth
                variant="contained"
                color="primary"
                disabled={!!application || isLoading}
              >
                {t('sourceJob.sendInvite')}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ApplyForm;
